import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, User } from "lucide-react";
import { fetchArticle, fetchBlog } from "@/lib/api";
import LoadingState from "@/components/LoadingState";

export default function BlogDetail() {
    const { slug } = useParams();
    const [article, setArticle] = useState(null);
    const [related, setRelated] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setLoading(true);
        setNotFound(false);
        fetchArticle(slug)
            .then(a => {
                setArticle(a);
                return fetchBlog({ limit: 20 });
            })
            .then(list => setRelated((list || []).filter(a => a.slug !== slug).slice(0, 3)))
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [slug]);

    if (loading) return <LoadingState label="Loading article" />;
    if (notFound || !article) return (
        <div className="container-tight py-24 text-center">
            <h1 className="font-display text-4xl text-white">Article not found</h1>
            <Link to="/blog" className="btn-primary mt-6">Back to blog</Link>
        </div>
    );

    const date = new Date(article.published_at);
    const dateStr = date.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

    return (
        <div data-testid="blog-detail-page">
            <div className="container-tight pt-8">
                <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors" data-testid="back-to-blog">
                    <ArrowLeft className="h-4 w-4" /> All articles
                </Link>
            </div>

            <article className="container-tight pt-8 pb-16 max-w-3xl">
                <div className="flex items-center gap-3 text-[11px] tracking-[0.22em] uppercase">
                    <span className="text-blue-400">{article.category}</span>
                    <span className="text-zinc-600">·</span>
                    <span className="text-zinc-500">{dateStr}</span>
                </div>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.02] mt-6" data-testid="article-title">
                    {article.title}
                </h1>
                <p className="text-lg md:text-xl text-zinc-400 mt-6 leading-relaxed">{article.excerpt}</p>
                <div className="mt-8 flex items-center gap-5 text-sm text-zinc-500 pb-8 border-b border-zinc-800">
                    <span className="inline-flex items-center gap-2"><User className="h-4 w-4" /> {article.author}</span>
                    <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4" /> {article.read_minutes} min read</span>
                </div>

                <div className="aspect-[16/9] my-10 bg-zinc-900 overflow-hidden">
                    <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
                </div>

                <div className="prose prose-invert max-w-none">
                    {article.body.split("\n\n").map((p, i) => (
                        <p key={i} className="text-zinc-200 leading-relaxed text-base md:text-lg mb-6">{p}</p>
                    ))}
                </div>
            </article>

            {related.length > 0 && (
                <section className="border-t border-zinc-800 py-16 md:py-20">
                    <div className="container-tight">
                        <div className="eyebrow">Related</div>
                        <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-white mt-2 mb-8">More from the blog</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {related.map(a => (
                                <Link
                                    key={a.id}
                                    to={`/blog/${a.slug}`}
                                    data-testid={`related-${a.slug}`}
                                    className="group card-flat overflow-hidden hover:border-zinc-600 transition-colors duration-200"
                                >
                                    <div className="aspect-video bg-zinc-900 overflow-hidden">
                                        <img src={a.image} alt={a.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                    </div>
                                    <div className="p-5">
                                        <div className="text-[10px] tracking-[0.2em] uppercase text-blue-400">{a.category}</div>
                                        <div className="font-display text-base font-semibold text-white mt-2 leading-snug group-hover:text-blue-400 transition-colors">{a.title}</div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}
